import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Menu, X, ChevronDown, Download } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { authAPI } from '../lib/api';
import { SearchBar } from './SearchBar';
import { MarketWeatherTicker } from './MarketWeatherTicker';
import { DarkModeToggle } from './theme/DarkModeToggle';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { categories } from '../lib/utils';
import { useLocation } from 'react-router-dom';

export function Header() {
  const { user, isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoriesOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        setCategoriesOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = () => {
    // Attempt to revoke refresh token on server, then clear local state
    (async () => {
      try {
        const refreshToken = useAuthStore.getState().refreshToken;
        if (refreshToken) {
          await authAPI.logout(refreshToken);
        }
      } catch (e) {
        // ignore errors
      } finally {
        logout();
        navigate('/');
      }
    })();
  };

  const handleCategoryClick = (category: string) => {
    const targetUrl = `/?category=${encodeURIComponent(category)}#category-filter`;
    navigate(targetUrl);

    setMobileMenuOpen(false);
    setCategoriesOpen(false);
  };

  const handleDownloadPDF = () => {
    navigate('/pdf-download');
  };

  const handleMobileNavigate = (path: string) => {
    navigate(path);
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-50"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Gradient backdrop */}
      <div className="absolute inset-0 h-full backdrop-blur-md bg-gradient-to-b from-white/70 dark:from-dark-900/90 to-gray-100/50 dark:to-dark-900/70 border-b border-gray-200/30 dark:border-primary-900/30" />

      <div className="relative container mx-auto px-4 py-2.5 md:py-3">
        <div className="flex items-center justify-between">
          {/* Logo - Text only */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Link to="/" className="flex items-center gap-2 group">
              <span className="font-serif text-xl font-bold text-transparent bg-clip-text bg-gradient-text">
                Radar Jovem
              </span>
            </Link>
          </motion.div>

          {/* Search beside the brand on desktop */}
          <motion.div
            className="newspaper-search-desktop hidden md:block"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <SearchBar />
          </motion.div>

          {/* Navigation - Desktop */}
          <nav className="newspaper-navigation hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative group"
            >
              Notícias
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
            </Link>

            {/* Categories Dropdown */}
            <div className="relative group">
              <button
                type="button"
                onClick={() => setCategoriesOpen((value) => !value)}
                onMouseEnter={() => setCategoriesOpen(true)}
                onMouseLeave={() => setCategoriesOpen(false)}
                className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative flex items-center gap-1"
                aria-haspopup="menu"
                aria-expanded={categoriesOpen}
                aria-controls="desktop-categories-menu"
              >
                Categorias
                <ChevronDown size={16} className={`transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
              </button>

              {/* Dropdown Menu */}
              {categoriesOpen && (
                <motion.div
                  id="desktop-categories-menu"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  onMouseEnter={() => setCategoriesOpen(true)}
                  onMouseLeave={() => setCategoriesOpen(false)}
                  className="absolute top-full left-0 mt-0 w-48 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-600 py-2 z-50"
                >
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategoryClick(category)}
                      className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors font-medium"
                    >
                      {category}
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            <Link
              to="/opiniao"
              className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative group"
            >
              Comentadores
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
            </Link>

            <Link
              to="/about"
              className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative group"
            >
              Sobre
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
            </Link>

            <Link
              to="/contact"
              className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative group"
            >
              Contato
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
            </Link>

            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative group flex items-center gap-2"
              title="Baixar Edições"
            >
              <Download size={16} />
              Baixar Edições
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
            </button>

            {isAuthenticated && ['admin', 'editor'].includes(user?.role || '') && (
              <Link
                to="/admin"
                className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative group"
              >
                {user?.role === 'editor' ? 'Painel' : 'Admin'}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
              </Link>
            )}

            {isAuthenticated && user?.role === 'writer' && user?.approval_status === 'approved' && (
              <Link
                to="/writer"
                className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 font-medium transition-all duration-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 relative group"
              >
                Writer
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-orange group-hover:w-full transition-all duration-300" />
              </Link>
            )}
          </nav>

          {/* Right section */}
          <div className="flex items-center gap-3 md:gap-4">
            <MarketWeatherTicker />

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="newspaper-theme-toggle">
                <DarkModeToggle />
              </div>
            </motion.div>

            {isAuthenticated ? (
              <motion.div
                className="hidden sm:flex items-center gap-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Bem-vindo</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {user?.name}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="btn-danger p-2 hover:scale-110"
                  title="Sair"
                >
                  <LogOut size={18} />
                </button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="hidden sm:flex items-center gap-2"
              >
                <Link
                  to="/login"
                  className="btn-ghost"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-standard"
                >
                  Criar conta
                </Link>
              </motion.div>
            )}

            {/* Mobile menu toggle */}
            <motion.button
              type="button"
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              whileTap={{ scale: 0.95 }}
              aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation-menu"
            >
              {mobileMenuOpen ? (
                <X size={24} className="text-gray-900 dark:text-white" />
              ) : (
                <Menu size={24} className="text-gray-900 dark:text-white" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Search Bar - visible on all sizes */}
        <motion.div
          className={`newspaper-search-mobile mt-2.5 md:hidden ${mobileMenuOpen ? 'hidden' : ''}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <SearchBar />
        </motion.div>

        {/* Mobile Menu */}
        <motion.div
          id="mobile-navigation-menu"
          initial="hidden"
          animate={mobileMenuOpen ? 'visible' : 'hidden'}
          variants={{
            hidden: { opacity: 0, height: 0 },
            visible: { opacity: 1, height: 'auto' },
          }}
          className={`md:hidden mt-3 border-t border-white/20 dark:border-primary-900/20 pt-3 relative z-[60] overflow-hidden ${mobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!mobileMenuOpen}
        >
          <div className="space-y-2 max-h-[calc(100vh-9rem)] overflow-y-auto pr-1">
            <button
              type="button"
              onClick={() => handleMobileNavigate('/')}
              className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium"
            >
              Notícias
            </button>

            {/* Mobile Categories */}
            <div>
              <button
                type="button"
                onClick={() => setCategoriesOpen(!categoriesOpen)}
                className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium flex items-center justify-between"
                aria-haspopup="menu"
                aria-expanded={categoriesOpen}
                aria-controls="mobile-categories-menu"
              >
                Categorias
                <ChevronDown size={16} className={`transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
              </button>

              {categoriesOpen && (
                <div id="mobile-categories-menu" className="ml-4 mt-2 space-y-1 border-l-2 border-primary-200 dark:border-primary-900 pl-4">
                  {categories.map((category) => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => handleCategoryClick(category)}
                      className="w-full text-left px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors text-sm"
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleMobileNavigate('/opiniao')}
              className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium"
            >
              Comentadores
            </button>

            <button
              type="button"
              onClick={() => handleMobileNavigate('/about')}
              className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium"
            >
              Sobre
            </button>

            <button
              type="button"
              onClick={() => handleMobileNavigate('/contact')}
              className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium"
            >
              Contato
            </button>

            <button
              type="button"
              onClick={() => {
                handleDownloadPDF();
                setMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium flex items-center gap-2"
            >
              <Download size={16} />
              Baixar Edições
            </button>

            {isAuthenticated && ['admin', 'editor'].includes(user?.role || '') && (
              <button
                type="button"
                onClick={() => handleMobileNavigate('/admin')}
                className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium"
              >
                {user?.role === 'editor' ? 'Painel' : 'Admin'}
              </button>
            )}

            {isAuthenticated && user?.role === 'writer' && user?.approval_status === 'approved' && (
              <button
                type="button"
                onClick={() => handleMobileNavigate('/writer')}
                className="w-full text-left px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors font-medium"
              >
                Writer
              </button>
            )}

            {!isAuthenticated && (
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => handleMobileNavigate('/login')}
                  className="w-full px-4 py-2 border border-primary-300/60 dark:border-primary-700/60 text-primary-700 dark:text-primary-300 rounded-lg font-semibold text-center hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-300"
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => handleMobileNavigate('/register')}
                  className="w-full px-4 py-2 bg-gradient-orange text-white rounded-lg font-semibold text-center hover:shadow-glow-orange transition-all duration-300"
                >
                  Criar conta
                </button>
              </div>
            )}
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors font-medium"
              >
                Sair
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </motion.header>
  );
}
